import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import MessageComposer from './MessageComposer';
import { MessageAttachments } from './MessageAttachments';
import { Markdown } from '../../utils/markdown';

export default function ThreadPanel() {
    const { activeThread, closeThread, _decryptMessages, authToken } = useApp();
    const [replies, setReplies] = useState<any[]>([]);
    const [panelWidth, setPanelWidth] = useState(390);
    const [isDragging, setIsDragging] = useState(false);

    // Decrypt parent and replies whenever activeThread changes
    useEffect(() => {
        if (!activeThread) return;
        const decryptedReplies = _decryptMessages(activeThread.replies || []);
        setReplies(decryptedReplies);
    }, [activeThread, _decryptMessages]);

    // Handle drag-to-resize
    useEffect(() => {
        if (!isDragging) return;
        const onMouseMove = (e: MouseEvent) => {
            const newWidth = window.innerWidth - e.clientX;
            setPanelWidth(Math.max(280, Math.min(700, newWidth)));
        };
        const onMouseUp = () => setIsDragging(false);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging]);

    if (!activeThread) return null;

    const parent = activeThread.parent;
    // Decrypt the parent message for display
    const [decryptedParent] = _decryptMessages([parent]);

    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <aside className="thread-panel" style={{ width: panelWidth, flexShrink: 0, position: 'relative' }}>
            {/* Resize handle on left edge */}
            <div
                onMouseDown={() => setIsDragging(true)}
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    cursor: 'ew-resize',
                    zIndex: 10,
                    background: isDragging ? 'var(--color-primary)' : 'transparent',
                    transition: 'background 0.15s',
                }}
                title="Drag to resize"
            />

            {/* Header */}
            <header className="thread-panel-header">
                <h2 className="thread-panel-title">Thread</h2>
                <button
                    className="thread-close-btn"
                    onClick={closeThread}
                    title="Close thread"
                >
                    ✕
                </button>
            </header>

            {/* Parent Message */}
            <div className="thread-parent-message">
                <div className="message">
                    <div className="message-avatar" style={{ background: getAvatarColor(parent.author_id || parent.author?.id) }}>
                        {parent.author?.avatar ? (
                            <img src={parent.author.avatar} alt={parent.author.name} />
                        ) : (
                            getInitials(parent.author?.name)
                        )}
                    </div>
                    <div className="message-content">
                        <div className="message-header">
                            <span className="message-author">{parent.author?.name}</span>
                            <span className="message-time">
                                {formatDate(parent.created_at)} at {formatTime(parent.created_at)}
                            </span>
                        </div>
                        <div className="message-text">
                            <Markdown text={decryptedParent.content || ''} />
                            <MessageAttachments attachments={parent.attachments || []} authToken={authToken} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Replies */}
            <div className="thread-replies">
                {replies.length > 0 && (
                    <div className="replies-count">
                        {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                    </div>
                )}

                {replies.map((reply: any) => (
                    <div key={reply.id} className="message">
                        <div className="message-avatar" style={{ background: getAvatarColor(reply.author_id || reply.author?.id) }}>
                            {reply.author?.avatar ? (
                                <img src={reply.author.avatar} alt={reply.author.name} />
                            ) : (
                                getInitials(reply.author?.name)
                            )}
                        </div>
                        <div className="message-content">
                            <div className="message-header">
                                <span className="message-author">{reply.author?.name}</span>
                                <span className="message-time">{formatTime(reply.created_at)}</span>
                            </div>
                            <div className="message-text">
                                <Markdown text={reply.content || ''} />
                                <MessageAttachments attachments={reply.attachments || []} authToken={authToken} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Reply Input */}
            <div className="thread-reply-input" style={{ borderTop: '1px solid var(--border-color)', padding: 16 }}>
                <MessageComposer
                    isThread={true}
                    placeholder="Reply to thread..."
                    submitLabel="Reply"
                />
            </div>
        </aside>
    );
}
